import type { PresetItem, PresetSpecs, TemplateStep } from '../types/api.js';

export interface ConfigSummaryRow {
  label: string;
  value: string;
}

export function getPresetConfigSteps(item: PresetItem | null | undefined): TemplateStep[] {
  if (!item || !Array.isArray(item.configSteps)) return [];
  return item.configSteps.filter((s) => s && s.stepType !== 'gift');
}

export function presetHasConfigurator(item: PresetItem | null | undefined): boolean {
  if (item?.hasConfigurator === true) return true;
  return getPresetConfigSteps(item).length > 0;
}

export function getPresetBasePriceRub(item: PresetItem | null | undefined): number | null {
  if (!item) return null;
  if (item.basePriceRub != null && Number.isFinite(item.basePriceRub) && item.basePriceRub > 0) {
    return Math.round(item.basePriceRub);
  }
  const digits = String(item.price || '')
    .replace(/[^\d]/g, '');
  const n = Number(digits);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function findOption(steps: TemplateStep[], stepId: string, optionId: string) {
  const step = steps.find((s) => String(s.id) === String(stepId));
  if (!step) return null;
  return step.options.find((o) => String(o.id) === String(optionId)) || null;
}

export function normalizeConfigChoices(
  raw: Record<string, string> | null | undefined,
  steps: TemplateStep[]
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const step of steps) {
    const sid = String(step.id);
    const picked = raw && raw[sid] != null ? String(raw[sid]) : '';
    const opt = picked ? findOption(steps, sid, picked) : null;
    if (opt) {
      out[sid] = String(opt.id);
      continue;
    }
    const def = step.options.find((o) => o.isDefault) || step.options[0];
    if (def) out[sid] = String(def.id);
  }
  return out;
}

export function resolveConfigPriceRub(
  item: PresetItem,
  choices: Record<string, string>
): number | null {
  const base = getPresetBasePriceRub(item);
  if (base == null) return null;
  const steps = getPresetConfigSteps(item);
  const normalized = normalizeConfigChoices(choices, steps);
  let total = base;
  for (const step of steps) {
    const opt = findOption(steps, String(step.id), normalized[String(step.id)]);
    if (opt && Number.isFinite(opt.priceDelta)) total += Math.round(opt.priceDelta);
  }
  return total > 0 ? total : null;
}

export function resolveConfigSpecs(
  item: PresetItem,
  choices: Record<string, string>
): PresetSpecs {
  const base = { ...(item.specs || {}) } as PresetSpecs;
  const steps = getPresetConfigSteps(item);
  const normalized = normalizeConfigChoices(choices, steps);
  const out: PresetSpecs = { ...base };
  for (const step of steps) {
    const opt = findOption(steps, String(step.id), normalized[String(step.id)]);
    if (!opt) continue;
    if (step.stepType === 'ram') out.ram = opt.label;
    if (step.stepType === 'ssd') out.storage = opt.label;
    if (step.stepType === 'cooling') out.cooler = opt.label;
    if (step.stepType === 'case') out.caseName = opt.label;
    if (step.stepType === 'color') out.buildColor = opt.label;
    if (opt.linkedSpecs) Object.assign(out, opt.linkedSpecs);
  }
  return out;
}

export function buildConfigSummary(
  item: PresetItem,
  choices: Record<string, string>
): ConfigSummaryRow[] {
  const rows: ConfigSummaryRow[] = [];
  const title = String(item.title || '').trim();
  if (title) rows.push({ label: 'Игровой ПК', value: title });
  const steps = getPresetConfigSteps(item);
  const normalized = normalizeConfigChoices(choices, steps);
  for (const step of steps) {
    const opt = findOption(steps, String(step.id), normalized[String(step.id)]);
    if (!opt) continue;
    rows.push({ label: step.title, value: opt.label });
  }
  return rows;
}

export function buildConfigCartLineKey(templateId: string, choices: Record<string, string>): string {
  const parts = Object.keys(choices)
    .sort()
    .map((k) => `${k}:${choices[k]}`);
  return `${templateId}#${parts.join('|')}`;
}

export function parseTemplateIdFromCartLineId(lineId: string): string {
  const s = String(lineId || '');
  const idx = s.indexOf('#');
  return idx >= 0 ? s.slice(0, idx) : s;
}
